// @LTG-ID: data/journals/selling-a-manufacturing-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 36 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-manufacturing-business",
    FRONTMATTER: {
        number: "73",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Manufacturing Business: What Buyers of Job Shops and Contract Manufacturers Pay For",
        deck: "Manufacturing businesses sell on customer diversity, equipment condition, skilled workforce depth, certifications, and backlog quality.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-28",
        publishedHuman: "June 28, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 448,
        timeRequired: "PT3M",
        keywords: "selling a manufacturing business, machine shop valuation, contract manufacturer sale, selling a job shop",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Manufacturing businesses sell on customer diversity, equipment condition, skilled workforce depth, certifications, and backlog quality. Customer concentration is the sector's defining risk, since job shops often grow around a few anchor accounts. Buyers include competitors, private equity platforms, and individuals, each weighing the plant's transferability differently. Manufacturing sellers hold an advantage most service businesses envy: hard assets, contracts, and capabilities a buyer can insp",
        complianceNote: "This article is educational and does not constitute legal or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "customer-concentration-selling-business",
        "management-team-business-value",
        "does-growth-increase-business-value"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Manufacturing Business: Job Shops & Contract Mfrs",
        canonical: "https://letstalkgrowth.com/journals/selling-a-manufacturing-business",
        ogImage: "/journals/og/selling-a-manufacturing-business.jpg",
    },
    BODY_MARKDOWN: `Manufacturing businesses sell on customer diversity, equipment condition, skilled workforce depth, certifications, and backlog quality. Customer concentration is the sector's defining risk, since job shops often grow around a few anchor accounts. Buyers include competitors, private equity platforms, and individuals, each weighing the plant's transferability differently.

Manufacturing sellers hold an advantage most service businesses envy: hard assets, contracts, and capabilities a buyer can inspect and verify. They also hold the small business world's most concentrated version of its most dangerous risk.

## The concentration question, asked first

Job shops and contract manufacturers naturally grow around anchor customers; a few strong accounts with steady releases are the sector's normal shape. Buyers know this, and they also know what it means: the first analysis run on any manufacturing target is revenue by customer, and everything in our customer concentration article applies here with the volume turned up. The moderating factors matter enormously in this sector: long-term supply agreements and blanket purchase orders that are assignable, multi-year relationships embedded in the customer's own qualified-vendor systems, and parts where switching suppliers would force the customer through expensive requalification. Concentration inside those protections reads very differently than concentration on a handshake, and sellers who paper their anchor relationships years ahead sell a materially different risk.

## What the walk-through is really assessing

When a buyer tours the plant, four inventories are running simultaneously. Equipment: age, condition, maintenance records, and whether capacity supports growth or is already the constraint, with deferred maintenance read as a hidden price reduction. Workforce: the skilled machinists, welders, and operators whose scarcity every owner knows, evaluated for depth, tenure, and documentation of their tribal knowledge, team depth in steel-toed form. Certifications and quality systems: the ISO and industry-specific qualifications that gate access to the customers the business serves, checked for currency and for whether they survive an ownership change intact. And backlog: not just its size but its quality, firm orders versus forecasts, margins on the booked work, and how far visibility extends.

## Who buys plants, and what each pays for

Strategic buyers, often competitors or complementary manufacturers, pay for capacity, capabilities, and customer lists they can integrate. Private equity platforms have been active consolidators in precision machining and niche manufacturing, paying for durable earnings and management that stays. Individual buyers take the smaller shops, financed through the SBA channel this series has covered, with everything that implies about provable, tax-return-visible earnings. As everywhere, the wider the pool a business qualifies for, the better the auction, and qualification is mostly the preparation this Journal keeps describing: documented earnings, papered customers, a bench beyond the owner, and a plant whose condition matches its books.`,
    FAQ: [
        { question: "What is the biggest value risk in selling a job shop?", answer: "Customer concentration. Buyers analyze revenue by customer first, and unprotected anchor accounts drive discounts, earnouts, or withdrawal. Assignable supply agreements and embedded vendor qualifications substantially moderate the risk." },
        { question: "Do buyers care more about my equipment or my customers?", answer: "Both, differently: customers and backlog establish the earnings story, while equipment condition and capacity determine what it costs to sustain it. Deferred maintenance is treated as a deduction from price, visible on the walk-through." },
        { question: "Do quality certifications transfer when a business sells?", answer: "Certifications attach to the company and its systems, and generally continue through a properly structured sale, though registrars and key customers may require notification or review. Confirm the mechanics for your specific certifications early in planning." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Customer Concentration: How Much Is Too Much When Selling a Business?",
            meta: "3 min · Brief 50",
            to: "/journals/customer-concentration-selling-business",
        },
        {
            tag: "Value Drivers",
            title: "Team Depth: Building a Management Bench That Buyers Will Pay For",
            meta: "3 min · Brief 52",
            to: "/journals/management-team-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Does Growth Increase Business Value? What Buyers Actually Pay For",
            meta: "3 min · Brief 54",
            to: "/journals/does-growth-increase-business-value",
        },
    ],
};

export default BRIEF;
